import dayjs from 'dayjs';

import { DATE_FORMAT } from 'utils/constants';

/** Mirrors `InspectionRecency` in the damages service (`lib/util/handlers/util.ts`). */
export type OrderableInspection = {
  id?: string | null;
  inspectionDate?: string | null;
  created?: string | null;
};

type InspectionAccessor<T> = (item: T) => OrderableInspection | null | undefined;

const parseDate = (value?: string | null): dayjs.Dayjs | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = dayjs(value);

  return parsed.isValid() ? parsed : undefined;
};

/** Undefined when the two are equal, so the caller falls through to the next key. */
const compareTimes = (left?: number, right?: number): number | undefined => {
  if (left !== undefined && right !== undefined) {
    return left === right ? undefined : left - right;
  }

  if (left !== undefined) {
    return 1;
  }

  return right !== undefined ? -1 : undefined;
};

export const compareIds = (left: string, right: string): number => {
  if (left === right) {
    return 0;
  }

  return left < right ? -1 : 1;
};

export const getInspectionDayKey = (
  inspection: OrderableInspection | null | undefined
): string | undefined => parseDate(inspection?.inspectionDate)?.format(DATE_FORMAT);

/**
 * Oldest to newest. `inspectionDate` is day-granular and a Turbine can be inspected twice in a
 * day, so `created` then id break the tie, matching the damages service's
 * `(inspection_date, created_at, global_id)` ranking. Missing values sort oldest.
 *
 * The id is compared as code units rather than with `localeCompare`, which sorts an uppercase
 * id after a lowercase one and varies with the runtime's locale data.
 */
export const byInspectionRecency = (
  left: OrderableInspection | null | undefined,
  right: OrderableInspection | null | undefined
): number =>
  compareTimes(
    parseDate(left?.inspectionDate)?.valueOf(),
    parseDate(right?.inspectionDate)?.valueOf()
  ) ??
  compareTimes(parseDate(left?.created)?.valueOf(), parseDate(right?.created)?.valueOf()) ??
  compareIds(left?.id ?? '', right?.id ?? '');

export const sortByInspectionRecency = <T>(items: T[], getInspection: InspectionAccessor<T>): T[] =>
  [...items].sort((left, right) => byInspectionRecency(getInspection(left), getInspection(right)));

export const sortByInspectionRecencyDesc = <T>(
  items: T[],
  getInspection: InspectionAccessor<T>
): T[] => sortByInspectionRecency(items, getInspection).reverse();

/**
 * "Previous" means the previous inspection *date*, not the previous row: same-day Inspections
 * are siblings with no change to show. Matches `criticalDamageCounts` in the damages service.
 */
export const findPreviousDayItem = <T>(
  items: T[],
  current: T,
  getInspection: InspectionAccessor<T>
): T | undefined => {
  const currentDay = getInspectionDayKey(getInspection(current));

  if (!currentDay) {
    return undefined;
  }

  const earlierItems = items.filter(item => {
    const day = getInspectionDayKey(getInspection(item));

    return !!day && day < currentDay;
  });

  return sortByInspectionRecency(earlierItems, getInspection).pop();
};

/** Days carrying more than one Inspection — the only days worth qualifying in the UI. */
export const getAmbiguousInspectionDays = (
  inspections: (OrderableInspection | null | undefined)[]
): Set<string> => {
  const idsByDay = new Map<string, Set<string>>();

  for (const inspection of inspections) {
    const day = getInspectionDayKey(inspection);

    if (!day || !inspection?.id) {
      continue;
    }

    idsByDay.set(day, (idsByDay.get(day) ?? new Set<string>()).add(inspection.id));
  }

  return new Set([...idsByDay.entries()].filter(([, ids]) => ids.size > 1).map(([day]) => day));
};
